// src/components/Activities.js
import React, { useRef } from "react";
import styled from "styled-components";
import { motion, useInView } from "framer-motion";
import { FaExternalLinkAlt } from "react-icons/fa";
import trampoline from "../assets/trampoline.webp";
import bowling from "../assets/bowling.webp";
import vrGames from "../assets/vr-games.webp";
import arcade from "../assets/arcade.webp";
import adventure from "../assets/adventure.webp";
import laserTag from "../assets/laser-tag.webp";

const activities = [
  { 
    name: "Trampoline Park", 
    img: trampoline,
    description: "Bounce away on our massive trampolines. Perfect for all ages with freestyle jumping, dodgeball, and slam dunk zones." 
  },
  { 
    name: "Bowling Alley", 
    img: bowling,
    description: "Strike it big on our state-of-the-art bowling lanes with automatic scoring and glow-in-the-dark night sessions." 
  },
  { 
    name: "VR Gaming", 
    img: vrGames,
    description: "Step into different worlds with our premium VR experiences. From epic adventures to intense shooters." 
  },
  { 
    name: "Arcade Games", 
    img: arcade,
    description: "Classic and modern arcade games with ticket rewards. Challenge friends or aim for the high score!" 
  },
  { 
    name: "Adventure Zone", 
    img: adventure,
    description: "Navigate challenging obstacles, rope courses, climbing walls, and more in our multi-level adventure zone." 
  },
  { 
    name: "Laser Tag Arena", 
    img: laserTag,
    description: "Epic team battles in our neon-lit arena with advanced laser equipment and interactive obstacles." 
  }
];

const ActivitiesSection = styled.section`
  background-color: #0a0a0a;
  position: relative;
  overflow: hidden;
  id: "activities";

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(255, 153, 0, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(0, 255, 204, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 10px;
  }
`;

const Card = styled(motion.div)`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  height: 400px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  transition: all 0.5s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.5s ease;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 20%, transparent 60%);
    z-index: 1;
  }
  
  ${Card}:hover & img {
    transform: scale(1.1);
  }
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 30px;
  z-index: 2;
  transition: all 0.5s ease;
  
  ${Card}:hover & {
    transform: translateY(-20px);
  }
`;

const CardTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #ff9900;
  margin-bottom: 15px;
  text-transform: uppercase;
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 1px;
  text-shadow: 0 0 10px rgba(255, 153, 0, 0.5);
`;

const CardDescription = styled.p`
  font-size: 16px;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.6;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease;
  
  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CardLink = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  color: #ff9900;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease 0.1s;
  font-weight: 600;
  
  svg {
    margin-left: 8px;
    font-size: 14px;
  }
  
  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Activities = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.1 });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <ActivitiesSection id="activities" ref={sectionRef}>
      <Container>
        <h2 className="section-title" data-aos="fade-up">Our Activities</h2>
        
        <Grid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {activities.map((activity, index) => (
            <Card key={index} variants={itemVariants}>
              <CardImage>
                <img src={activity.img} alt={activity.name} />
              </CardImage>
              <CardContent>
                <CardTitle>{activity.name}</CardTitle>
                <CardDescription>{activity.description}</CardDescription>
                <CardLink>
                  Learn More <FaExternalLinkAlt />
                </CardLink>
              </CardContent>
            </Card>
          ))}
        </Grid>
      </Container>
    </ActivitiesSection>
  );
};

export default Activities;